import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Form, message } from "antd";
import { getRoles, createRole, getPermissions, updateRole, deleteRole } from "../../services/roles";
import { IRole } from "../../types/interfaces";
import useModal from "../../hooks/useModal";
import useAuth from "../../hooks/useAuth";

export default function useRoles() {
  const queryClient = useQueryClient();
  const roleModal = useModal();
  const { userData } = useAuth();
  const [form] = Form.useForm();

  const { data: roles, isLoading: rolesLoading } = useQuery({
    queryKey: ["roles"],
    queryFn: getRoles,
  });

  const { data: permissions, isLoading: permissionsLoading } = useQuery({
    queryKey: ["permissions"],
    queryFn: getPermissions,
  });

  const createRoleMutation = useMutation({
    mutationFn: createRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      message.success("Rol creado exitosamente");
      roleModal.closeModal();
      form.resetFields();
    },
    onError: (error: any) => {
      message.error(error?.response?.data || "Error al crear el rol");
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => updateRole(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      message.success("Rol actualizado exitosamente");
      roleModal.closeModal();
      form.resetFields();
    },
    onError: (error: any) => {
      message.error(error?.response?.data || "Error al actualizar el rol");
    },
  });

  const deleteRoleMutation = useMutation({
    mutationFn: deleteRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      message.success("Rol eliminado exitosamente");
    },
    onError: (error: any) => {
      message.error(error?.response?.data || "Error al eliminar el rol");
    },
  });

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (roleModal.editing && roleModal.id) {
        await updateRoleMutation.mutateAsync({
          id: roleModal.id,
          data: {
            name: values.name,
            description: values.description || "",
            permissions: values.permissions || [],
          },
        });
      } else {
        await createRoleMutation.mutateAsync({
          ...values,
          is_system: false,
          is_assignable_by_company: true,
        });
      }
    } catch (error) {
      console.log("Validation Failed:", error);
    }
  };

  const handleOpenModal = () => {
    roleModal.setEditing(false);
    roleModal.openModal();
    form.resetFields();
  };

  const handleEdit = (role: IRole) => {
    roleModal.setEditing(true);
    roleModal.setId(role.id);
    roleModal.openModal();
    form.setFieldsValue({
      name: role.name,
      description: role.description,
      permissions: role.edges?.permissions?.map((p: any) => p.name),
    });
  };

  const handleDelete = async (id: number) => {
    await deleteRoleMutation.mutateAsync(id);
  };

  return {
    roles,
    rolesLoading,
    permissions,
    permissionsLoading,
    roleModal,
    form,
    handleOk,
    handleOpenModal,
    handleEdit,
    handleDelete,
    userData,
  };
}
